import React, { forwardRef } from "react";
import VenueCard from "./VenueCard";
import DatePanel from "./DatePanel";

const temperatureLabels = {
  cool: "Cool (65-69 °F)",
  warm: "Warm (70-75 °F)",
  hot: "Hot (76-85 °F)",
};
const humidityLabels = { humid: "Humid", dry: "Dry" };
const sunCloudsLabels = {
  clear: "Clear Skies",
  romanticClouds: "Romantic Clouds",
};
const windLabels = { calm: "Calm", breeze: "Light Breeze" };
const precipLabels = { yes: "Yes", no: "No" };

const dateKey = (v) => {
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
    return String(v);
  }
  const d = new Date(v);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return String(v);
};

export default forwardRef(function PreferencesResults(
  {
    forecastResults,
    preferences,
    selectedMatches,
    onSelectDate,
    onBack,
    nextDisabled,
    onContinue,
    apiError,
    plan,
  },
  ref
) {
  const isEssentials = plan === "essentials";
  const pickWord = isEssentials ? "one" : "any";
  const tailCopy = isEssentials ? "." : " – pick as many as you want.";

  return (
    <div ref={ref} className="preferences-page-container">
      {apiError && <p className="error">{apiError}</p>}

      <h2 className="page-title">Your Matching Dates & Venues</h2>

      <section className="results-intro">
        <h3>How to Use This Page</h3>
        <p>
          Each venue below shows a calendar of the date range you chose. Click{" "}
          <em>{pickWord}</em> {isEssentials ? "date" : "dates"} you’d like to
          track
          {tailCopy}
        </p>

        <ul className="legend">
          <li>
            <span className="legend-color poor" /> Poor match
          </li>
          <li>
            <span className="legend-color weak" /> Weak match
          </li>
          <li>
            <span className="legend-color good" /> Good match
          </li>
        </ul>

        <p>
          When you’re done, scroll down and hit <em>Continue to Dashboard</em>{" "}
          to save your selections and see live forecasts. Need to tweak
          anything? Click <em>Edit Preferences</em> to go back.
        </p>

        <h3>Your Selected Preferences</h3>
        <ul className="preferences-summary">
          {preferences.temperature && (
            <li>
              <strong>Temperature:</strong>{" "}
              {temperatureLabels[preferences.temperature]}
            </li>
          )}
          {preferences.humidity && (
            <li>
              <strong>Humidity:</strong> {humidityLabels[preferences.humidity]}
            </li>
          )}
          {preferences.sunClouds && (
            <li>
              <strong>Sun / Clouds:</strong>{" "}
              {sunCloudsLabels[preferences.sunClouds]}
            </li>
          )}
          {preferences.sunset && (
            <li>
              <strong>Sunset no earlier than:</strong> {preferences.sunset}
            </li>
          )}
          {preferences.wind && (
            <li>
              <strong>Wind:</strong> {windLabels[preferences.wind]}
            </li>
          )}
          {preferences.precipitation && (
            <li>
              <strong>Light precipitation desired:</strong>{" "}
              {precipLabels[preferences.precipitation]}
            </li>
          )}
          <li>
            <strong>Date range:</strong> {preferences.startDate} →{" "}
            {preferences.endDate}
          </li>
        </ul>
      </section>

      <div className="venues-bucket">
        {forecastResults.map(({ venue, matches }, idx) => {
          // ✅ Normalize selected dates so they match calendar cell keys
          const selectedDates = (selectedMatches[venue.name] || []).map((m) =>
            dateKey(m.forecast_date)
          );

          return (
            <div className="venue-row" key={venue.name}>
              <VenueCard
                id={idx === 0 ? "pref-venue-card" : undefined}
                venue={venue}
                matches={matches}
                startDate={preferences.startDate}
                endDate={preferences.endDate}
                onSelectDate={(m) => onSelectDate(venue.name, m)}
                selectedDates={selectedDates}
              />
              {!!selectedDates.length && (
                <DatePanel
                  id={idx === 0 ? "pref-date-panel" : undefined}
                  matches={matches}
                  selectedDates={selectedDates}
                  onClear={(m) => onSelectDate(venue.name, m)}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="navigation-buttons-container preferences-footer">
        <button className="btn btn-secondary" onClick={onBack}>
          Edit Preferences
        </button>
        <button
          className={`btn btn-primary${nextDisabled ? " btn-disabled" : ""}`}
          disabled={nextDisabled}
          onClick={onContinue}
        >
          Continue to Dashboard
        </button>
      </div>
    </div>
  );
});
